import React, { Component, useEffect } from "react";
import withSizes from "react-sizes";
import MetaTags from 'react-meta-tags';

import {
  Header,
  SectionIntro,
  SectionCareer,
  TeamList,
  Button,
  Modal
} from "../../components";
import { useDispatch, useSelector } from 'react-redux'
import style from "./Team.module.scss";
import pattern from "../../assets/patterns/pattern01.svg";
import useLocale from "../../hooks/useLocale";
import { loadTeamData } from "../../store/slices/team";
import { normalizeMedia, mapSeo } from "../../api/strapi";

const FALLBACK_COMPANY_INTRO = {
  title: 'Creating the Best Architecture Firm',
  description: 'The secret is a smoothly working team of disparate people who are each endowed with some special talent. Who come together like a symphony to create beautiful music.',
  image: '/assets/company-intro.png',
  buttonLabel: 'About Company',
  buttonUrl: null,
}

function HookWrapper({
  render
}) {
  const locale = useLocale();
  const dispatch = useDispatch();

  useEffect(() => {
    dispatch(loadTeamData(locale));
  }, [dispatch, locale]);

  return render(loadIntro(), loadDivisions(), loadCompanyIntro(locale), loadSeo(), locale);
}

function loadIntro() {
  const { data } = useSelector(state => state.team)
  const banner = data && data.banner
  return {
    title: banner && banner.bannerTitle,
    caption: banner && banner.bannerDescription,
    image: (normalizeMedia(banner && banner.bannerImage) || {}).file_url,
  }
}

function loadDivisions() {
  const { data } = useSelector(state => state.team)
  const divisions = (data && data.divisions) || []
  return divisions.map(d => ({
    division: d.name,
    member: (d.members || []).map(m => ({
      name: m.name,
      position: m.position,
      photo: (normalizeMedia(m.photo) || {}).file_url,
    })),
  }))
}

function loadCompanyIntro(locale) {
  const { data } = useSelector(state => state.team)
  const intro = data && data.companyIntro
  const media = normalizeMedia(intro && intro.image)
  const button = intro && intro.button

  return {
    title: (intro && intro.title) || FALLBACK_COMPANY_INTRO.title,
    description: (intro && intro.description) || FALLBACK_COMPANY_INTRO.description,
    image: (media && media.file_url) || FALLBACK_COMPANY_INTRO.image,
    buttonLabel: (button && button.label) || FALLBACK_COMPANY_INTRO.buttonLabel,
    buttonUrl: (button && button.url) || `/${locale}/about`,
  }
}

function loadSeo() {
  const { data } = useSelector(state => state.team)
  return mapSeo(data && data.seo)
}

class Team extends Component {
  constructor(props) {
    super(props);

    this.state = {
      modal: false,
      currentData: null
    }
  }

  _openModal = (data) => {
    this.setState({
      modal: true,
      currentData: data
    })
  }

  _closeModal = () => {
    this.setState({
      modal: false,
      currentData: null
    })
  }

  render() {
    const { modal, currentData } = this.state;
    const { isMobile } = this.props;
    return (
      <HookWrapper render={(intro, divisions, companyIntro, seo, locale) =>
        <>
         <MetaTags>
            <title>{seo.title}</title>
            <meta name="description" content={seo.meta_description} />
            <meta name="keyword" content={seo.meta_keyword} />
            <meta property="og:title" content={seo.title} />
          </MetaTags>
          <Header />
          <SectionIntro title={intro.title} caption={intro.caption} image={intro.image} pattern={pattern} />
          <section className={style.team__content}>
            {
              divisions.map(_el => (
                <TeamList data={_el} onClick={this._openModal} />
              ))}
          </section>
          <section className={style.company__intro}>
            <div className={style.intro__inner}>
              <div className={style.intro__left}>
                <img src={companyIntro.image} className={style.intro__image} alt="" />
              </div>
              <div className={style.intro__right}>
                <div className={style.intro__title}>{companyIntro.title}</div>
                <p className={style.intro__text}>{companyIntro.description}</p>
                <div className={style.button__panel}>
                  <Button
                    buttonName={companyIntro.buttonLabel}
                    type="redirect"
                    link={companyIntro.buttonUrl}
                    full={isMobile}
                  />
                </div>
              </div>
            </div>
          </section>
          <SectionCareer locale={locale} />
          <Modal visible={modal} onClose={this._closeModal} data={currentData} />
        </>
      } />
    )
  }
}

const mapStateToProps = ({ width }) => ({
  isMobile: width < 500
})

export default withSizes(mapStateToProps)(Team);
